import { useEffect, useState } from 'react';
import { useSettings } from '@wu/widget-core';
import type { A11yState } from '../../types';
import { EXCLUSION_RELATED_CLASSES } from '../constants';
import { applyFontSize } from '../utils/apply-font-size';
import { applyTextSpacing } from '../utils/apply-text-spacing';
import { isElementInsideWidget } from '../utils/is-element-inside-widget';

const FONT_SIZE_ATTR = 'data-eapps-font-size';
const LINE_HEIGHT_ATTR = 'data-eapps-line-height';
const LETTER_SPACING_ATTR = 'data-eapps-letter-spacing';
const OBSERVED_ATTRIBUTE = 'class';

export function useApplyInlineStyles(
  { largeText, extraTextSpacing }: A11yState,
  elements: HTMLElement[]
) {
  const { editMode } = useSettings();
  const [domVersion, setDomVersion] = useState(0);

  useEffect(() => {
    if (!editMode) {
      return;
    }

    const observer = new MutationObserver((mutations) => {
      if (mutations.some(isDemoClassMutation)) {
        setDomVersion((version) => version + 1);
      }
    });

    observer.observe(document.body, {
      subtree: true,
      attributes: true,
      attributeOldValue: true,
      attributeFilter: [OBSERVED_ATTRIBUTE]
    });

    return () => observer.disconnect();
  }, [editMode]);

  useEffect(() => {
    const connectedElements = elements.filter(
      (element) => element instanceof HTMLElement && element.isConnected
    );

    if (!editMode) {
      applyFontSize(connectedElements, !!largeText);
      applyTextSpacing(connectedElements, !!extraTextSpacing);

      return;
    }

    const { externalElements, excludedElements } =
      splitElementsByScope(elements);

    // Always rollback before re-applying so baseline attributes
    // are never captured from already transformed values.
    resetTextStyles(externalElements);
    externalElements.forEach(ensureTextStyleAttributes);
    applyFontSize(externalElements, !!largeText);
    applyTextSpacing(externalElements, !!extraTextSpacing);

    // Excluded areas should stay untouched in "Before"/controls mode.
    // Restore baseline values to avoid inheriting transformed parent styles.
    resetTextStyles(excludedElements);
    restoreTextStylesFromAttributes(excludedElements);
  }, [elements, largeText, extraTextSpacing, domVersion, editMode]);
}

function isDemoClassMutation(mutation: MutationRecord) {
  if (
    mutation.type !== 'attributes' ||
    mutation.attributeName !== OBSERVED_ATTRIBUTE ||
    !(mutation.target instanceof HTMLElement)
  ) {
    return false;
  }

  const currentClassName = mutation.target.className;
  const previousClassName = mutation.oldValue ?? '';

  return EXCLUSION_RELATED_CLASSES.some(
    (className) =>
      currentClassName.includes(className) ||
      previousClassName.includes(className)
  );
}

function splitElementsByScope(elements: HTMLElement[]) {
  return elements.reduce(
    (accumulator, element) => {
      if (!(element instanceof HTMLElement) || !element.isConnected) {
        return accumulator;
      }

      if (isElementInsideWidget(element)) {
        accumulator.excludedElements.push(element);
      } else {
        accumulator.externalElements.push(element);
      }

      return accumulator;
    },
    {
      externalElements: [] as HTMLElement[],
      excludedElements: [] as HTMLElement[]
    }
  );
}

function resetTextStyles(elements: HTMLElement[]) {
  applyFontSize(elements, false);
  applyTextSpacing(elements, false);
}

function ensureTextStyleAttributes(element: HTMLElement) {
  const computedStyle = window.getComputedStyle(element);
  const fontSize = parseFloat(computedStyle.fontSize);
  const lineHeight = parseFloat(computedStyle.lineHeight);
  const letterSpacing = parseFloat(computedStyle.letterSpacing);

  setBaselineStyleAttribute(
    element,
    FONT_SIZE_ATTR,
    fontSize,
    computedStyle.fontSize,
    isNaN(fontSize)
  );
  setBaselineStyleAttribute(
    element,
    LINE_HEIGHT_ATTR,
    lineHeight,
    computedStyle.lineHeight,
    isNaN(lineHeight)
  );
  setBaselineStyleAttribute(
    element,
    LETTER_SPACING_ATTR,
    isNaN(letterSpacing) ? 0 : letterSpacing,
    `${isNaN(letterSpacing) ? 0 : letterSpacing}px`
  );
}

function restoreTextStylesFromAttributes(elements: HTMLElement[]) {
  elements.forEach((element) => {
    if (!(element instanceof HTMLElement) || !element.isConnected) {
      return;
    }

    const fontSize = element.getAttribute(FONT_SIZE_ATTR);
    const lineHeight = element.getAttribute(LINE_HEIGHT_ATTR);
    const letterSpacing = element.getAttribute(LETTER_SPACING_ATTR);

    element.style.fontSize = fontSize ? `${fontSize}px` : '';
    element.style.lineHeight = lineHeight ? `${lineHeight}px` : '';
    element.style.letterSpacing = letterSpacing ? `${letterSpacing}px` : '';
  });
}

function setBaselineStyleAttribute(
  element: HTMLElement,
  attrName: string,
  numericValue: number,
  styleValue: string,
  shouldSkip = false
) {
  if (shouldSkip || element.hasAttribute(attrName)) {
    return;
  }

  element.setAttribute(attrName, numericValue.toString());

  if (attrName === FONT_SIZE_ATTR) {
    element.style.fontSize = styleValue;
    return;
  }

  if (attrName === LINE_HEIGHT_ATTR) {
    element.style.lineHeight = styleValue;
    return;
  }

  element.style.letterSpacing = styleValue;
}
